import {useTranslation} from "react-i18next";

import * as Yup from "yup";
import * as Const from "./PaymentRequestConstants";

import {addFilter, getFilterValue, renderMultiSelect} from "../../../../../utils/FilterUtils";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_ACTIVE_FILTERS} from "../../atoms/OutstandingManagementAtoms";
import AdvancedFilter from "../../../../../component/AdvancedFilter";
import {asEndOfDay, asStartOfDay,} from "../../../../../utils/FinanceDateUtils";

import * as Renders from "../../../../../component/AdvancedFiltersRenders";
import {useLocation, useNavigate} from "react-router-dom";
import {useContext, useEffect, useState} from "react";
import {GlobalContext} from "../../../../../../../util/state/GlobalContextProvider";

function OutstandingManagementFilters(props: any) {
    const {t} = useTranslation("finance", {keyPrefix: "payments.payment-request"});

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get('customerId');
    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const {t: tg} = useTranslation("translation");
    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
                value: x.name,
                label: tg(`legalEntities.${x.name}`),
            })
        );
    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const navigate = useNavigate();
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        [Const.ID_FILTER_CUSTOMER_ID]: Yup.string()
            .nullable()
            .max(128, t("filters.errors.customerId")),
        [Const.ID_FILTER_SAP_INVOICE_NUMBER]: Yup.string()
            .nullable()
            .max(128, t("filters.errors.sapNumber")),
        [Const.ID_FILTER_AMOUNT_FROM]: Yup.number().transform((value, originalValue) => {
            return typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('amount', t('filters.errors.invalid-amount'), function (value) {
            const toValue = this.parent[Const.ID_FILTER_AMOUNT_TO];
            return toValue === undefined || value === undefined || value <= toValue;
        }).nullable(),
        [Const.ID_FILTER_AMOUNT_TO]: Yup.number().transform((value, originalValue) => {
            return typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : parseFloat(originalValue);
        }).test('amount', t('filters.errors.invalid-amount'), function (value) {
            const fromValue = this.parent[Const.ID_FILTER_AMOUNT_FROM];
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
        [Const.ID_FILTER_DUE_DATE_FROM]: Yup.date()
            .nullable()
            .test(
                "testdueDateFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dueDateto ||
                        value <= context.parent.dueDateto
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_DUE_DATE_TO]: Yup.date()
            .nullable()
            .test(
                "testdueDateTo",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dueDateFrom ||
                        value >= context.parent.dueDateFrom
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_INVOICE_DATE_FROM]: Yup.date()
            .nullable()
            .test(
                "testInvoiceFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.invoiceDateTo ||
                        value <= context.parent.invoiceDateTo
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_INVOICE_DATE_TO]: Yup.date()
            .nullable()
            .test("testInvoiceTo", t("filters.errors.invalid-date"), (value, context) => {
                return (
                    !value ||
                    !context.parent.invoiceDateFrom ||
                    value >= context.parent.invoiceDateFrom
                );
            })
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_PAN_CARD]: Yup.string()
            .nullable()
            .max(128, t("filters.errors.maskeredPan")),
        [Const.ID_FILTER_CREATIONDATEFROM]: Yup.date()
            .nullable()
            .test(
                "testCreationFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.toCreationDate ||
                        value <= context.parent.toCreationDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_CREATIONDATETO]: Yup.date()
            .nullable()
            .test(
                "testCreationTo",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.fromCreationDate ||
                        value >= context.parent.fromCreationDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_MODIFICATIONDATEFROM]: Yup.date()
            .nullable()
            .test(
                "testModificationFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.toModificationDate ||
                        value <= context.parent.toModificationDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_MODIFICATIONDATETO]: Yup.date()
            .nullable()
            .test(
                "testModificationTo",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.fromModificationDate ||
                        value >= context.parent.fromModificationDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_LASTTRANSACTIONDATEFROM]: Yup.date()
            .nullable()
            .test(
                "testTransactionFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.toTranasctionDate ||
                        value <= context.parent.toTranasctionDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_LASTTRANSACTIONDATETO]: Yup.date()
            .nullable()
            .test(
                "testTransactionTo",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.fromTransactionDate ||
                        value >= context.parent.fromTransactionDate
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        [Const.ID_FILTER_DEALERS]: Yup.array(),
        [Const.ID_FILTER_LEGAL_ENTITIES]: Yup.array().min(1, 'message.validation.selectAtLeastOne'),
    });

    const defaultValues = {
        [Const.ID_FILTER_PAYMENT_TYPE]: getFilterValue(Const.ID_FILTER_PAYMENT_TYPE, filterDefs) || "",
        [Const.ID_FILTER_PAYMENT_MODE]: getFilterValue(Const.ID_FILTER_PAYMENT_MODE, filterDefs) || "",
        [Const.ID_FILTER_AMOUNT_TYPE]: getFilterValue(Const.ID_FILTER_AMOUNT_TYPE, filterDefs) || [],
        [Const.ID_FILTER_STATUSES_OUTSTANDING]: getFilterValue(Const.ID_FILTER_STATUSES_OUTSTANDING, filterDefs) || [],
        [Const.ID_FILTER_AMOUNT_FROM]: getFilterValue(Const.ID_FILTER_AMOUNT_FROM, filterDefs) || "",
        [Const.ID_FILTER_AMOUNT_TO]: getFilterValue(Const.ID_FILTER_AMOUNT_TO, filterDefs) || "",
        [Const.ID_FILTER_DUE_DATE_FROM]: getFilterValue(Const.ID_FILTER_DUE_DATE_FROM, filterDefs) || null,
        [Const.ID_FILTER_DUE_DATE_TO]: getFilterValue(Const.ID_FILTER_DUE_DATE_TO, filterDefs) || null,
        [Const.ID_FILTER_INVOICE_DATE_FROM]: getFilterValue(Const.ID_FILTER_INVOICE_DATE_FROM, filterDefs) || null,
        [Const.ID_FILTER_INVOICE_DATE_TO]: getFilterValue(Const.ID_FILTER_INVOICE_DATE_TO, filterDefs) || null,
        [Const.ID_FILTER_SAP_INVOICE_NUMBER]: getFilterValue(Const.ID_FILTER_SAP_INVOICE_NUMBER, filterDefs) || "",
        [Const.ID_FILTER_CUSTOMER_ID]: getFilterValue(Const.ID_FILTER_CUSTOMER_ID, filterDefs) || (customerId ? customerId : ""),
        [Const.ID_FILTER_PAN_CARD]: getFilterValue(Const.ID_FILTER_PAN_CARD, filterDefs) || "",
        [Const.ID_FILTER_CREATIONDATEFROM]: getFilterValue(Const.ID_FILTER_CREATIONDATEFROM, filterDefs) || null,
        [Const.ID_FILTER_CREATIONDATETO]: getFilterValue(Const.ID_FILTER_CREATIONDATETO, filterDefs) || null,
        [Const.ID_FILTER_MODIFICATIONDATEFROM]: getFilterValue(Const.ID_FILTER_MODIFICATIONDATEFROM, filterDefs) || null,
        [Const.ID_FILTER_MODIFICATIONDATETO]: getFilterValue(Const.ID_FILTER_MODIFICATIONDATETO, filterDefs) || null,
        [Const.ID_FILTER_CUSTOMER_NAME]: getFilterValue(Const.ID_FILTER_CUSTOMER_NAME, filterDefs) || "",
        [Const.ID_FILTER_CUSTOMER_LASTNAME]: getFilterValue(Const.ID_FILTER_CUSTOMER_LASTNAME, filterDefs) || "",
        [Const.ID_FILTER_COMPANY_NAME]: getFilterValue(Const.ID_FILTER_COMPANY_NAME, filterDefs) || "",
        [Const.ID_FILTER_CORRELATION_NAME]: getFilterValue(Const.ID_FILTER_CORRELATION_NAME, filterDefs) || [],
        [Const.ID_FILTER_CORRELATION_ID]: getFilterValue(Const.ID_FILTER_CORRELATION_ID, filterDefs) || "",
        [Const.ID_FILTER_INVOICE_NUMBER]: getFilterValue(Const.ID_FILTER_INVOICE_NUMBER, filterDefs) || "",
        [Const.ID_FILTER_LASTTRANSACTIONDATEFROM]: getFilterValue(Const.ID_FILTER_LASTTRANSACTIONDATEFROM, filterDefs) || null,
        [Const.ID_FILTER_LASTTRANSACTIONDATETO]: getFilterValue(Const.ID_FILTER_LASTTRANSACTIONDATETO, filterDefs) || null,
        [Const.ID_FILTER_LEGAL_ENTITIES]:
        getFilterValue(Const.ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
        legalEntityOptions.map((x: any) => x.value),
        [Const.ID_FILTER_DEALERS]: getFilterValue(Const.ID_FILTER_DEALERS, filterDefs) || []
    };


    const decimalInputProps = {
        inputMode: 'decimal',
        pattern: '^\\d+(\\.\\d{1,2})?$'
    };
    const fields = {
        main: {
            [Const.ID_FILTER_PAYMENT_TYPE]: Renders.renderSelect(
                Const.ID_FILTER_PAYMENT_TYPE,
                t("filters." + Const.ID_FILTER_PAYMENT_TYPE),
                Const.outstandingTypes.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            [Const.ID_FILTER_AMOUNT_TYPE]: Renders.renderMultiSelect(
                Const.ID_FILTER_AMOUNT_TYPE,
                t("filters." + Const.ID_FILTER_AMOUNT_TYPE),
                Const.amountTypes.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            [Const.ID_FILTER_STATUSES_OUTSTANDING]: Renders.renderMultiSelect(
                Const.ID_FILTER_STATUSES_OUTSTANDING,
                t("filters." + Const.ID_FILTER_STATUSES_OUTSTANDING),
                Const.outstanding_statuses.map((x) => {
                    return {...x, label: t(x.label)};
                })
            )
        },
        adv: {
            [Const.ID_FILTER_DUE_DATE_FROM]: Renders.renderDatePickerFrom(
                Const.ID_FILTER_DUE_DATE_FROM,
                t("filters." + Const.ID_FILTER_DUE_DATE_FROM)
            ),
            [Const.ID_FILTER_DUE_DATE_TO]: Renders.rendereDatePickerTo(
                Const.ID_FILTER_DUE_DATE_TO,
                t("filters." + Const.ID_FILTER_DUE_DATE_TO)
            ),
            [Const.ID_FILTER_INVOICE_DATE_FROM]: Renders.renderDatePickerFrom(
                Const.ID_FILTER_INVOICE_DATE_FROM,
                t("filters." + Const.ID_FILTER_INVOICE_DATE_FROM)
            ),
            [Const.ID_FILTER_INVOICE_DATE_TO]: Renders.rendereDatePickerTo(
                Const.ID_FILTER_INVOICE_DATE_TO,
                t("filters." + Const.ID_FILTER_INVOICE_DATE_TO)
            ),
            [Const.ID_FILTER_CREATIONDATEFROM]: Renders.renderDatePickerFrom(
                Const.ID_FILTER_CREATIONDATEFROM,
                t("filters." + Const.ID_FILTER_CREATIONDATEFROM),
                true
            ),
            [Const.ID_FILTER_CREATIONDATETO]: Renders.rendereDatePickerTo(
                Const.ID_FILTER_CREATIONDATETO,
                t("filters." + Const.ID_FILTER_CREATIONDATETO),
                true
            ),
            [Const.ID_FILTER_MODIFICATIONDATEFROM]: Renders.renderDatePickerFrom(
                Const.ID_FILTER_MODIFICATIONDATEFROM,
                t("filters." + Const.ID_FILTER_MODIFICATIONDATEFROM),
                true
            ),
            [Const.ID_FILTER_MODIFICATIONDATETO]: Renders.rendereDatePickerTo(
                Const.ID_FILTER_MODIFICATIONDATETO,
                t("filters." + Const.ID_FILTER_MODIFICATIONDATETO),
                true
            ),
            [Const.ID_FILTER_SAP_INVOICE_NUMBER]: Renders.renderTextField(
                Const.ID_FILTER_SAP_INVOICE_NUMBER,
                t("filters." + Const.ID_FILTER_SAP_INVOICE_NUMBER),
                "text"
            ),
            [Const.ID_FILTER_INVOICE_NUMBER]: Renders.renderTextField(
                Const.ID_FILTER_INVOICE_NUMBER,
                t("filters." + Const.ID_FILTER_INVOICE_NUMBER),
                "text"
            ),
            [Const.ID_FILTER_LASTTRANSACTIONDATEFROM]: Renders.renderDatePickerFrom(
                Const.ID_FILTER_LASTTRANSACTIONDATEFROM,
                t("filters." + Const.ID_FILTER_LASTTRANSACTIONDATEFROM),
                true
            ),
            [Const.ID_FILTER_LASTTRANSACTIONDATETO]: Renders.rendereDatePickerTo(
                Const.ID_FILTER_LASTTRANSACTIONDATETO,
                t("filters." + Const.ID_FILTER_LASTTRANSACTIONDATETO),
                true
            ),
            [Const.ID_FILTER_PAYMENT_MODE]: Renders.renderSelect(
                Const.ID_FILTER_PAYMENT_MODE,
                t("filters." + Const.ID_FILTER_PAYMENT_MODE),
                Const.paymentTMode.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            [Const.ID_FILTER_PAN_CARD]: Renders.renderTextField(
                Const.ID_FILTER_PAN_CARD,
                t("filters." + Const.ID_FILTER_PAN_CARD),
                "text"
            ),
            [Const.ID_FILTER_CORRELATION_NAME]: Renders.renderSelect(
                Const.ID_FILTER_CORRELATION_NAME,
                t("filters." + Const.ID_FILTER_CORRELATION_NAME),
                Const.correlationName.map((x) => {
                    return {...x, label: t(x.label)};
                })
            ),
            [Const.ID_FILTER_CORRELATION_ID]: Renders.renderTextField(
                Const.ID_FILTER_CORRELATION_ID,
                t("filters." + Const.ID_FILTER_CORRELATION_ID),
                "text"
            ),
            [Const.ID_FILTER_CUSTOMER_ID]: Renders.renderTextField(
                Const.ID_FILTER_CUSTOMER_ID,
                t("filters." + Const.ID_FILTER_CUSTOMER_ID),
                "text"
            ),
            [Const.ID_FILTER_CUSTOMER_NAME]: Renders.renderTextField(
                Const.ID_FILTER_CUSTOMER_NAME,
                t("filters." + Const.ID_FILTER_CUSTOMER_NAME),
                "text"
            ),
            [Const.ID_FILTER_CUSTOMER_LASTNAME]: Renders.renderTextField(
                Const.ID_FILTER_CUSTOMER_LASTNAME,
                t("filters." + Const.ID_FILTER_CUSTOMER_LASTNAME),
                "text"
            ),
            [Const.ID_FILTER_COMPANY_NAME]: Renders.renderTextField(
                Const.ID_FILTER_COMPANY_NAME,
                t("filters." + Const.ID_FILTER_COMPANY_NAME),
                "text"
            ),
            [Const.ID_FILTER_AMOUNT_FROM]: Renders.renderTextField(
                Const.ID_FILTER_AMOUNT_FROM,
                t("filters." + Const.ID_FILTER_AMOUNT_FROM), "number", decimalInputProps
            ),
            [Const.ID_FILTER_AMOUNT_TO]: Renders.renderTextField(
                Const.ID_FILTER_AMOUNT_TO,
                t("filters." + Const.ID_FILTER_AMOUNT_TO), "number", decimalInputProps
            ),
            [Const.ID_FILTER_LEGAL_ENTITIES]: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
        },
    };

    useEffect(() => {

        if (customerId) {
            addFilter(
                setFilters,
                Const.ID_FILTER_CUSTOMER_ID, customerId
            );
        }
    }, [])
    const submit = (data: any) => {
        Object.keys(defaultValues)
            .filter(
                (param) =>
                    ![
                        Const.ID_FILTER_DUE_DATE_FROM,
                        Const.ID_FILTER_DUE_DATE_TO,
                        Const.ID_FILTER_INVOICE_DATE_FROM,
                        Const.ID_FILTER_INVOICE_DATE_TO,
                    ].includes(param)
            )
            .forEach((param) => {
                addFilter(setFilters, param, data[param]);
            });
        addFilter(
            setFilters,
            Const.ID_FILTER_DUE_DATE_FROM,
            asStartOfDay(data[Const.ID_FILTER_DUE_DATE_FROM])
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_DUE_DATE_TO,
            asEndOfDay(data[Const.ID_FILTER_DUE_DATE_TO])
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_INVOICE_DATE_FROM,
            asStartOfDay(data[Const.ID_FILTER_INVOICE_DATE_FROM])
        );
        addFilter(
            setFilters,
            Const.ID_FILTER_INVOICE_DATE_TO,
            asEndOfDay(data[Const.ID_FILTER_INVOICE_DATE_TO])
        );
        addFilter(setFilters, Const.ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, Const.ID_FILTER_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);

        if (customerId) {
            queryParams.delete('customerId');
            const newUrl = `${location.pathname}`;
            navigate(newUrl);
        }

    };

    return (
        <AdvancedFilter
            schema={schema}
            mainFields={fields.main}
            fields={fields.adv}
            defaultValues={defaultValues}
            customDefaultValues={Const.customDefaultValuesOutstanding}
            onSubmit={submit}
            onReset={reset}
        />
    );
}

export default OutstandingManagementFilters;
